/* eslint-disable react-hooks/exhaustive-deps */
import { Box, FormControl, Grid, InputAdornment, InputLabel, MenuItem, Paper, Select, TextField, Typography } from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { formatDateForInput } from 'utilities/common';
import { ZERO_GUID } from 'modules/enumerations/enumerationIds';
import { isIdEqual } from 'modules/enumerations/enumerationHelpers';
import AdvancedSearch from 'modules/common/advancedSearch.component';
import { CreateExpenseCodeSearchInfo, getExpenseCategoryId, getExpenseCategoryTitle } from 'modules/common/expenseCodeHelper';

const EXCEPTION_TYPE_IDS = {
  BENEFIT_EXTENSION: '9F6A7C8B-3D41-4C2A-9E8F-1B7D5A6C4E21',
  NO_COST_EXCEPTION: 'E2B94F17-6A5D-43C8-8F31-0A9D7C4B2E56',
  EXCEPTION: '4A3D9F62-5C18-47B1-AE92-6F8D0C1B5E34'
};

const safeNumber = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export const getMuiMultiSelectValue = value => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string')
    return value
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
  return [];
};

const ExceptionComment = props => {
  const { enumerations, commentFormData, clientExceptionApprovers = [], expenseCodes = [], setCommentFormData, isExceptionSentToClient } = props;

  const exceptionTypeOptions =
    enumerations && Array.isArray(enumerations.exceptionTypes)
      ? (enumerations?.exceptionTypes || []).filter(x => x?.isActive !== false)
      : [];

  const exceptionRequestTypeOptions = useMemo(() => {
    const requestTypes = enumerations?.exceptionRequestTypes || [];
    const activeRequestTypes = requestTypes.filter(x => x?.isActive !== false);
    const initialInactiveRequestType = commentFormData?.requestTypeId
      ? requestTypes.find(x => x?.isActive === false && isIdEqual(x?.id, commentFormData.requestTypeId))
      : null;

    return initialInactiveRequestType ? [...activeRequestTypes, initialInactiveRequestType] : activeRequestTypes;
  }, [commentFormData?.commentId]);

  const exceptionStatusOptions =
    enumerations && Array.isArray(enumerations.exceptionStatuses)
      ? (enumerations?.exceptionStatuses || []).filter(x => x?.isActive !== false)
      : [];

  const expenseCodeSearchInfo = useMemo(() => CreateExpenseCodeSearchInfo(expenseCodes), [expenseCodes]);

  const getExpenseCodeById = expenseCodeId =>
    expenseCodes.find(expenseCode => String(expenseCode?.expenseCodeId) === String(expenseCodeId));

  const selectedExpenseCode = getExpenseCodeById(commentFormData?.expenseCodeId);
  const selectedExpenseCategory = selectedExpenseCode?.category;
  const selectedExpenseCategoryLabel = getExpenseCategoryTitle(selectedExpenseCategory) || '';

  const exceptionTypeId = commentFormData?.exceptionTypeId || ZERO_GUID;

  let isBenefitExtension = isIdEqual(exceptionTypeId, EXCEPTION_TYPE_IDS.BENEFIT_EXTENSION);
  let isException = isIdEqual(exceptionTypeId, EXCEPTION_TYPE_IDS.EXCEPTION);

  let showRequestedAmount = isException;
  let showRequestedItem = isBenefitExtension;

  const actualCost = useMemo(() => {
    const items = commentFormData?.exceptionLineItems || [];
    return items.reduce((sum, li) => sum + safeNumber(li?.totalAmount ?? li?.totalAmount), 0);
  }, [commentFormData?.exceptionLineItems]);

  const approvedAmount = useMemo(() => {
    const items = commentFormData?.exceptionLineItems || [];
    return items.reduce((sum, li) => sum + safeNumber(li?.approvedAmount ?? li?.approvedAmount), 0);
  }, [commentFormData?.exceptionLineItems]);

  const exceptionApprovers = commentFormData?.exceptionApprovers || [];

  const selectedApproverIds1 = exceptionApprovers
    .filter(x => x?.exceptionGroupNumber === 1)
    .map(x => x?.exceptionApproverId)
    .filter(Boolean);

  const selectedApproverIds2 = exceptionApprovers
    .filter(x => x?.exceptionGroupNumber === 2)
    .map(x => x?.exceptionApproverId)
    .filter(Boolean);

  const selectedSet1 = new Set(selectedApproverIds1);
  const selectedSet2 = new Set(selectedApproverIds2);

  const optionsForApprover1 = clientExceptionApprovers.filter(a => !selectedSet2.has(a.id));
  const optionsForApprover2 = clientExceptionApprovers.filter(a => !selectedSet1.has(a.id));

  const setApproverGroupIds = (groupNumber, nextIds) => {
    const withoutGroup = exceptionApprovers.filter(a => a?.exceptionGroupNumber !== groupNumber);
    const nextEntries = (nextIds || []).map(id => ({
      exceptionGroupNumber: groupNumber,
      exceptionApproverId: id
    }));

    setCommentFormData({
      ...commentFormData,
      exceptionApprovers: [...withoutGroup, ...nextEntries]
    });
  };

  useEffect(() => {
    isBenefitExtension = isIdEqual(commentFormData?.exceptionTypeId, EXCEPTION_TYPE_IDS.BENEFIT_EXTENSION);
    isException = isIdEqual(commentFormData?.exceptionTypeId, EXCEPTION_TYPE_IDS.EXCEPTION);

    showRequestedAmount = isException;
    showRequestedItem = isBenefitExtension;
  }, [commentFormData?.exceptionTypeId]);

  const approvedByLabel =
    clientExceptionApprovers.find(a => a.id === commentFormData?.approvedById)?.title || commentFormData?.approvedById || '';

  return (
    <Grid container>
      <Grid item xs={12} sx={{ alignSelf: 'flex-end', paddingLeft: '16px' }}>
        <Paper
          sx={{
            padding: 2,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            backgroundColor: 'transparent',
            width: '100%'
          }}
        >
          <Typography variant="h6" sx={{ marginBottom: 2 }}>
            Exception
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="exceptionStatus">Status</InputLabel>
                <Select
                  labelId="exceptionStatus"
                  value={commentFormData?.statusId || ''}
                  onChange={e => setCommentFormData({ ...commentFormData, statusId: e.target.value })}
                  variant="standard"
                  disabled={true}
                >
                  {exceptionStatusOptions.map(opt => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="exceptionType">Exception Type</InputLabel>
                <Select
                  labelId="exceptionType"
                  value={exceptionTypeId}
                  onChange={e => setCommentFormData({ ...commentFormData, exceptionTypeId: e.target.value })}
                  variant="standard"
                  disabled={isExceptionSentToClient}
                >
                  {(exceptionTypeOptions || []).map(opt => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {showRequestedAmount && (
              <Grid item xs={4}>
                <TextField
                  label="Requested $"
                  name="exceptionRequestedMoney"
                  required
                  value={commentFormData?.requestedAmount ?? ''}
                  margin="dense"
                  variant="standard"
                  type="number"
                  InputLabelProps={{ required: false }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                  }}
                  onChange={e => setCommentFormData({ ...commentFormData, requestedAmount: e.target.value })}
                  disabled={isExceptionSentToClient}
                  sx={{ width: '100%' }}
                />
                <Typography variant="caption" display="block" color="primary">
                  Required
                </Typography>
              </Grid>
            )}
            {showRequestedItem && (
              <Grid item xs={4}>
                <TextField
                  label="Requested Item"
                  name="exceptionRequestedItem"
                  required
                  value={commentFormData?.requestedItem ?? ''}
                  margin="dense"
                  variant="standard"
                  fullWidth
                  InputLabelProps={{ required: false }}
                  onChange={e => setCommentFormData({ ...commentFormData, requestedItem: e.target.value })}
                  disabled={isExceptionSentToClient}
                />
                <Typography variant="caption" display="block" color="primary">
                  Required
                </Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                label="Exception Reason"
                name="exceptionReason"
                required
                value={commentFormData?.exceptionReason || ''}
                margin="dense"
                multiline
                rows={1}
                variant="standard"
                fullWidth
                InputLabelProps={{ required: false }}
                onChange={e => setCommentFormData({ ...commentFormData, exceptionReason: e.target.value })}
                disabled={isExceptionSentToClient}
              />
              <Typography variant="caption" display="block" color="primary">
                Required
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="approver1">Approver 1</InputLabel>
                <Select
                  labelId="approver1"
                  multiple
                  variant="standard"
                  value={selectedApproverIds1}
                  onChange={e => {
                    const ids = getMuiMultiSelectValue(e.target.value);
                    setApproverGroupIds(1, ids);
                  }}
                  disabled={isExceptionSentToClient}
                  renderValue={selectedIds =>
                    (selectedIds || []).map(id => clientExceptionApprovers.find(a => a.id === id)?.title || id).join(', ')
                  }
                >
                  {optionsForApprover1.map(a => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="approver2">Approver 2</InputLabel>
                <Select
                  labelId="approver2"
                  multiple
                  variant="standard"
                  value={selectedApproverIds2}
                  onChange={e => {
                    const ids = getMuiMultiSelectValue(e.target.value);
                    setApproverGroupIds(2, ids);
                  }}
                  disabled={isExceptionSentToClient}
                  renderValue={selectedIds =>
                    (selectedIds || []).map(id => clientExceptionApprovers.find(a => a.id === id)?.title || id).join(', ')
                  }
                >
                  {optionsForApprover2.map(a => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ marginTop: '8px' }}>
                <AdvancedSearch
                  labelText="Expense Code"
                  variant="standard"
                  displayValueAccessorKey={expenseCodeSearchInfo.nameKey}
                  saveIdValueAccessorKey={expenseCodeSearchInfo.idKey}
                  searchingCriteria={expenseCodeSearchInfo.searchingColumns}
                  searchingCriteriaExtensions={expenseCodeSearchInfo.searchingColumnExtensions}
                  searchingRows={expenseCodeSearchInfo.searchingRows}
                  id={selectedExpenseCode?.expenseCodeId || commentFormData?.expenseCodeId || ''}
                  value={selectedExpenseCode?.expenseCodeCode || ''}
                  disabled={isExceptionSentToClient}
                  setIdValue={expenseCodeId => {
                    const nextExpenseCodeId = Array.isArray(expenseCodeId) ? null : expenseCodeId;
                    const nextExpenseCode = getExpenseCodeById(nextExpenseCodeId);
                    setCommentFormData({
                      ...commentFormData,
                      expenseCodeId: nextExpenseCodeId,
                      categoryId: nextExpenseCode ? getExpenseCategoryId(nextExpenseCode.category) : null
                    });
                  }}
                  maxWidthsx="360px"
                />
              </Box>
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Category"
                name="exceptionCategory"
                value={selectedExpenseCategoryLabel}
                margin="dense"
                variant="standard"
                fullWidth
                disabled={isExceptionSentToClient}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="requestType">Request Type</InputLabel>
                <Select
                  labelId="requestType"
                  value={commentFormData?.requestTypeId || ''}
                  onChange={e => setCommentFormData({ ...commentFormData, requestTypeId: e.target.value })}
                  variant="standard"
                  disabled={isExceptionSentToClient}
                >
                  {exceptionRequestTypeOptions.map(opt => (
                    <MenuItem key={opt.id} value={opt.id} disabled={opt?.isActive === false}>
                      {opt.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Approved By"
                name="approvedBy"
                value={approvedByLabel}
                margin="dense"
                variant="standard"
                fullWidth
                InputProps={{ readOnly: true }}
                disabled={isExceptionSentToClient}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Approved Date"
                name="exceptionApprovedDate"
                type="date"
                value={formatDateForInput(commentFormData?.approvedDate)}
                margin="dense"
                variant="standard"
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{ readOnly: true }}
                disabled={isExceptionSentToClient}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Approved Amount"
                name="exceptionApprovedAmount"
                value={approvedAmount || 0}
                margin="dense"
                variant="standard"
                type="number"
                InputProps={{
                  readOnly: true,
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
                onChange={e => setCommentFormData({ ...commentFormData, approvedAmount: e.target.value })}
                disabled={isExceptionSentToClient}
                sx={{ width: '100%' }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Actual Cost"
                name="exceptionActualCost"
                value={actualCost || 0}
                margin="dense"
                variant="standard"
                type="number"
                InputProps={{
                  readOnly: true,
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
                disabled={isExceptionSentToClient}
                sx={{ width: '100%' }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Balance"
                name="balance"
                value={(actualCost || 0) - (approvedAmount || 0)}
                margin="dense"
                variant="standard"
                type="number"
                InputProps={{
                  readOnly: true,
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
                disabled={isExceptionSentToClient}
                sx={{ width: '100%' }}
              />
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
};

const mapStateToProps = state => ({});

export default compose(connect(mapStateToProps, {}))(ExceptionComment);